import { PartnerService } from '@duck/api-definitions'

const S = PartnerService.AssignmentDetailsStatus

export type AssignmentKind = 'single' | 'multi' | 'public'

export interface AssignmentGroup {
  assignment: PartnerService.Assignment
  kind: AssignmentKind
  details: PartnerService.AssignmentDetails[]
  completion: { submitted: number; total: number }
}

/** Public if it has a share link (can grow); else single/multi by personIds count. */
export const classifyAssignment = (a: PartnerService.Assignment): AssignmentKind => {
  if (a.linkToken) return 'public'
  return (a.personIds?.length ?? 0) > 1 ? 'multi' : 'single'
}

export const computeCompletion = (
  details: PartnerService.AssignmentDetails[]
): { submitted: number; total: number } => ({
  submitted: details.filter((d) => d.status === S.Submitted).length,
  total: details.length,
})

export const bestGradeForPerson = (
  personId: string,
  details: PartnerService.AssignmentDetails[]
): number | undefined => {
  const grades = details
    .filter((d) => d.personId === personId && typeof d.grade === 'number')
    .map((d) => d.grade as number)
  return grades.length ? Math.max(...grades) : undefined
}

export const buildAssignmentGroups = (
  assignments: PartnerService.Assignment[],
  details: PartnerService.AssignmentDetails[]
): { publicGroups: AssignmentGroup[]; sentGroups: AssignmentGroup[] } => {
  const detailsByAssignment = details.reduce<Record<string, PartnerService.AssignmentDetails[]>>(
    (acc, d) => {
      ;(acc[d.assignmentId] ??= []).push(d)
      return acc
    },
    {}
  )

  const groups: AssignmentGroup[] = assignments.map((assignment) => {
    const groupDetails = detailsByAssignment[assignment.id] ?? []
    return {
      assignment,
      kind: classifyAssignment(assignment),
      details: groupDetails,
      completion: computeCompletion(groupDetails),
    }
  })

  return {
    publicGroups: groups.filter((g) => g.kind === 'public'),
    sentGroups: groups.filter((g) => g.kind !== 'public'),
  }
}
